((root, factory) => {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  } else {
    root.QProductVariantButtonsCore = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, () => {
  const MODES = new Set(['auto', 'text', 'image']);

  const isObject = (value) => (
    value !== null && typeof value === 'object' && !Array.isArray(value)
  );

  const isNonEmptyString = (value) => (
    typeof value === 'string' && value.trim().length > 0
  );

  const isValidId = (value) => (
    (typeof value === 'number' && Number.isInteger(value) && value > 0)
    || (typeof value === 'string' && /^[1-9][0-9]*$/.test(value))
  );

  const normalizeOption = (option) => {
    if (!isObject(option) || !isNonEmptyString(option.name)) return null;
    if (!Array.isArray(option.values) || option.values.length === 0) return null;

    const values = [];
    const seenValues = new Set();

    for (const rawValue of option.values) {
      if (!isNonEmptyString(rawValue)) return null;

      const value = rawValue.trim();
      if (seenValues.has(value)) return null;

      seenValues.add(value);
      values.push(value);
    }

    return {
      name: option.name.trim(),
      values,
    };
  };

  const normalizeVariant = (variant, options) => {
    if (!isObject(variant) || !isValidId(variant.id)) return null;
    if (typeof variant.available !== 'boolean') return null;
    if (!Array.isArray(variant.options) || variant.options.length !== options.length) return null;
    if (variant.imageUrl !== null && typeof variant.imageUrl !== 'string') return null;

    const values = [];

    for (let index = 0; index < variant.options.length; index += 1) {
      const rawValue = variant.options[index];
      if (!isNonEmptyString(rawValue)) return null;

      const value = rawValue.trim();
      if (!options[index].values.includes(value)) return null;
      values.push(value);
    }

    return {
      id: String(variant.id),
      available: variant.available,
      options: values,
      imageUrl: variant.imageUrl === null ? '' : variant.imageUrl.trim(),
    };
  };

  const normalizeConfig = (raw) => {
    if (!isObject(raw)) return null;
    if (!Array.isArray(raw.options) || raw.options.length < 1 || raw.options.length > 3) {
      return null;
    }

    const options = raw.options.map(normalizeOption);
    if (options.some((option) => option === null)) return null;

    if (!Array.isArray(raw.variants) || raw.variants.length === 0) return null;

    const variants = raw.variants.map((variant) => normalizeVariant(variant, options));
    if (variants.some((variant) => variant === null)) return null;
    if (new Set(variants.map((variant) => variant.id)).size !== variants.length) return null;
    if (new Set(variants.map((variant) => JSON.stringify(variant.options))).size !== variants.length) {
      return null;
    }

    if (raw.optionModes != null && !Array.isArray(raw.optionModes)) return null;

    const optionModes = [options.length === 1 ? 'auto' : 'text'];
    for (let index = 1; index < options.length; index += 1) {
      const hasMode = Object.prototype.hasOwnProperty.call(raw.optionModes || [], index);
      if (!hasMode) {
        optionModes.push('auto');
        continue;
      }

      const mode = raw.optionModes[index];
      if (!MODES.has(mode)) return null;
      optionModes.push(mode);
    }

    return { options, optionModes, variants };
  };

  const candidatesForChoice = (config, index, value, selections) => (
    config.variants.filter((variant) => {
      for (let position = 0; position <= index; position += 1) {
        if (position === index) {
          if (variant.options[position] !== value) return false;
          continue;
        }

        const expected = selections[position];
        if (expected && variant.options[position] !== expected) return false;
      }

      return true;
    })
  );

  const findExactVariant = (variants, desired) => (
    variants.find((variant) => (
      variant.options.every((value, index) => value === desired[index])
    )) || null
  );

  const choiceState = (config, index, value, selections) => {
    const candidates = candidatesForChoice(config, index, value, selections);
    const desired = [...selections];
    desired[index] = value;

    const exactVariant = findExactVariant(candidates, desired);
    const selectionVariant = (exactVariant?.available ? exactVariant : null)
      || candidates.find((variant) => variant.available)
      || null;
    const imageVariant = (exactVariant?.imageUrl ? exactVariant : null)
      || candidates.find((variant) => variant.available && variant.imageUrl)
      || candidates.find((variant) => variant.imageUrl)
      || null;

    return {
      available: selectionVariant !== null,
      selected: selections[index] === value,
      selectionVariant,
      imageUrl: imageVariant?.imageUrl || '',
    };
  };

  const groupMode = (config, index, selections) => {
    if (index === 0 && config.options.length > 1) return 'text';

    const configuredMode = config.optionModes[index] || 'auto';
    if (configuredMode !== 'auto') return configuredMode;

    const hasRelevantImage = config.options[index].values.some((value) => (
      candidatesForChoice(config, index, value, selections).some((variant) => variant.imageUrl)
    ));

    return hasRelevantImage ? 'image' : 'text';
  };

  return { normalizeConfig, choiceState, groupMode };
});
